import { ShapeUtil, TLBaseShape, Rectangle2d, HTMLContainer } from 'tldraw'
import CommentPin from './CommentPin'
import { shapeNodeToSvgImage } from '../utils/shapeNodeToSvgImage'

export type CommentPinShape = TLBaseShape<
  'CommentPin',
  {
    w: number
    h: number
    commentId?: string
    resolved?: boolean
    hasReplies?: boolean
    unreadCount?: number
    authorName?: string
    locked?: boolean
  }
>

export const COMMENT_PIN_WIDTH = 40
export const COMMENT_PIN_HEIGHT = 40

export class CommentPinShapeUtil extends ShapeUtil<CommentPinShape> {
  static override type = 'CommentPin' as const

  getDefaultProps(): CommentPinShape['props'] {
    return {
      w: COMMENT_PIN_WIDTH,
      h: COMMENT_PIN_HEIGHT,
      resolved: false,
      hasReplies: false,
      unreadCount: 0,
    }
  }

  getGeometry(shape: CommentPinShape) {
    return new Rectangle2d({
      width: shape.props.w,
      height: shape.props.h,
      isFilled: true,
    })
  }

  component(shape: CommentPinShape) {
    if (!shape) return null

    const {
      props: { commentId, resolved, hasReplies, unreadCount, authorName },
    } = shape

    return (
      <HTMLContainer
        style={{
          width: `${shape.props.w}px`,
          height: `${shape.props.h}px`,
          pointerEvents: 'all',
        }}
      >
        <CommentPin
          commentId={commentId}
          resolved={resolved}
          hasReplies={hasReplies}
          unreadCount={unreadCount}
          authorName={authorName}
          x={shape.x}
          y={shape.y}
        />
      </HTMLContainer>
    )
  }

  indicator(shape: CommentPinShape) {
    return (
      <rect
        x={0}
        y={0}
        width={shape.props.w}
        height={shape.props.h}
        fill="none"
        stroke="#1a73e8"
        strokeWidth={2}
        strokeDasharray="4 2"
        rx={4}
        ry={4}
      />
    )
  }

  override getHandles() {
    // Comment pins are not resizable, so no resize handles
    return []
  }

  override canResize() {
    return false
  }

  override canEdit() {
    return false
  }

  // Note: tldraw's ShapeUtil has no canDelete hook, so this is a custom helper
  // rather than an override. Deletion is prevented elsewhere — comments must be
  // deleted through the popover's Delete button to ensure backend cleanup.
  canDelete() {
    return false
  }

  override isAspectRatioLocked() {
    return true
  }

  override hideSelectionBoundsFg() {
    return true
  }

  override hideSelectionBoundsBg() {
    return true
  }

  // FS-482: rasterize the live DOM for off-DOM exports. See shapeNodeToSvgImage.
  override toSvg(shape: CommentPinShape) {
    return shapeNodeToSvgImage(shape.id, shape.props.w, shape.props.h)
  }
}